import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { compressImage } from '../utils/compressImage';

import '../contracts/Orders_ItemsWithBothLaudosResponse';
import '../contracts/Orders_UploadAttributePhotoResponse';
import '../contracts/Orders_UploadAttributePhotoInput';
import type { Orders_BothLaudosItemRow } from '../contracts/Orders_BothLaudosItemRow';

interface BothLaudosDados {
  items: Orders_BothLaudosItemRow[];
  total: number;
}

interface BothLaudosResponse {
  success: boolean;
  dados?: BothLaudosDados;
  error?: string;
}

export function useSOLaudosComparison() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const items = ref<Orders_BothLaudosItemRow[]>([]);
  const total = ref(0);
  const basePath = '/index.php/so/orders';

  const fetchComparisonItems = async (params?: Record<string, string | number | boolean | null | undefined>) => {
    log('useSOLaudosComparison', 'fetchComparisonItems', 'Fetching items with both laudos', params);
    isLoading.value = true;
    error.value = null;
    try {
      const raw = await httpService.get(
        `${basePath}/items_with_both_laudos`,
        { responseContract: 'Orders_ItemsWithBothLaudosResponse', params }
      );
      const response = raw as BothLaudosResponse;
      if (response.success) {
        items.value = response.dados?.items || [];
        total.value = response.dados?.total || 0;
        return { items: items.value, total: total.value };
      }
      throw new Error(response.error || 'Erro ao buscar itens com ambos laudos');
    } catch (err: any) {
      logError('useSOLaudosComparison', 'fetchComparisonItems', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const uploadAttributePhoto = async (
    file: File,
    orderItemId: number | string,
    attributeId: number | string
  ): Promise<string> => {
    // Compress the laudo photo client-side (1920px max, 80% JPEG) before
    // upload — raw multi-MB phone photos time out on slow connections. On any
    // compression failure fall back to the original file so upload never breaks.
    let toUpload = file;
    try {
      toUpload = await compressImage(file);
    } catch (err) {
      logError('useSOLaudosComparison', 'uploadAttributePhoto', 'Compression failed, uploading original', err);
    }

    const formData = new FormData();
    formData.append('photo', toUpload);
    formData.append('order_item_id', String(orderItemId));
    formData.append('attribute_id', String(attributeId));

    const response = await httpService.post(
      `${basePath}/upload_attribute_photo`,
      formData,
      { responseContract: 'Orders_UploadAttributePhotoResponse', inputContract: 'Orders_UploadAttributePhotoInput' }
    ) as { success: boolean; dados?: { url: string }; error?: string };

    if (response.success && response.dados?.url) {
      return response.dados.url;
    }
    throw new Error(response.error || 'Upload failed');
  };

  return {
    isLoading,
    error,
    items,
    total,
    fetchComparisonItems,
    uploadAttributePhoto
  };
}
